import { action, observable, computed, makeObservable } from 'mobx';
import { configurationsStore, dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { IConfigurationDataManager } from 'typings/dataManagers';
import { userDataManager } from 'singletons';

export class AssignUserViewStore {
    @observable _selectedUserId: Nullable<string>;
    @observable _workEmail: Nullable<string>;
    @observable _displayName: Nullable<string>;
    @observable _selectedUserFromSuggestion: ISuggestedUser;
    @observable _openSelection: boolean;
    _masterAccountId: string;
    _configurationDataManager: IConfigurationDataManager;
    removeUserCallback: (userId?: string) => Promise<void>;
    saveUserCallback: (userId: string, previousUserId?: string) => Promise<void>;

    constructor(
        userId: string,
        email: string,
        displayName: string,
        masterAcountId: string,
        saveUserCallback: (userId: string, previousUserId?: string) => Promise<void>,
        removeUserCallback: (userId?: string) => Promise<void>
    ) {
        makeObservable(this);
        this._selectedUserId = userId;
        this._workEmail = email;
        this._displayName = displayName;
        this._masterAccountId = masterAcountId;
        this._configurationDataManager = dataManagerFactory.getConfigurationDataManager();
        this._openSelection = false;
        this.saveUserCallback = saveUserCallback;
        this.removeUserCallback =  removeUserCallback;
    }

    @computed
    get isEmpty(): boolean {
        return !this._selectedUserId && !this._openSelection;
    }

    @computed
    get isUserAssigned(): boolean {
        return !!this._selectedUserId && !this._openSelection;
    }

    get currentCycleId(): number {
        return configurationsStore.cycleSettingsId;
    }

    @computed
    get openSelection(): boolean {
        return this._openSelection;
    }

    @computed
    get selectedUserId(): Nullable<string> {
        return this._selectedUserId;
    }

    @computed
    get workEmail(): Nullable<string> {
        return this._workEmail;
    }

    @computed
    get selectedUserFromSuggestion(): ISuggestedUser {
        return this._selectedUserFromSuggestion;
    }

    @computed
    get displayName(): Nullable<string> {
        return this._displayName;
    }

    getSearchSuggestionText(user: ISuggestedUser): string {
        return user.displayName;
    }

    @action
    clearFoundUser(): void {
        this._selectedUserId = null;
    }

    async searchUsers(
        searchText: string, resultLimit: number, includeAll: boolean): Promise<Nullable<ISuggestedUser[]>> {
        return userDataManager.getSuggestedUsers(
            this._masterAccountId,
            searchText,
            resultLimit,
            includeAll
        );
    }

    @action
    setFoundUser(user: ISuggestedUser): void {
        this._selectedUserFromSuggestion = user;
    }

    @action
    cancel(): void {
        this._openSelection = false;
    }

    @action
    assignUser(): void {
        this._openSelection = true;
    }

    @action
    async save(): Promise<void> {
        if (!this._selectedUserFromSuggestion) {
            return;
        }

        this._openSelection = false;
        const initialSelectedUserId = this._selectedUserId;
        this._selectedUserId = this._selectedUserFromSuggestion.userId;
        this._workEmail = this._selectedUserFromSuggestion.emailAddress;
        this._displayName = this._selectedUserFromSuggestion.displayName;
        await this.saveUserCallback(this._selectedUserId, initialSelectedUserId ?? '');
    }

    @action
    async remove(): Promise<void> {
        if (!this._selectedUserId) {
            return;
        }
        await this.removeUserCallback(this._selectedUserId);
        this._selectedUserId = null;
        this._workEmail = null;
        this._displayName = null;
    }

    @action
    setUserId(userId: string): void {
        this._selectedUserId = userId;
    }

    @action
    setWorkEmail = (workEmail: string) => {
        this._workEmail = workEmail;
    }

    @action
    setDisplayName = (displayName: string) => {
        this._displayName = displayName;
    }
}